/** Multiselect-Dropdown mit Checkboxen.
 *  Erwartetes Markup:
 *    <div class="dropdown-check">
 *      <button type="button" class="dropdown-check-btn">Alle</button>
 *      <div class="dropdown-check-panel">
 *        <label class="dropdown-check-item"><input type="checkbox" value="x" /> Beschriftung</label>
 *      </div>
 *    </div>
 *  Die Button-Beschriftung fasst die Auswahl zusammen: nichts gewählt zeigt den
 *  ursprünglichen Text, eine Auswahl deren Beschriftung, mehrere "Erste +N".
 */

export interface DropdownCheckHandle {
  /** Setzt die Auswahl programmatisch. Loest bewusst kein onChange aus. */
  setValues(values: string[]): void;
  getValues(): string[];
}

export interface DropdownCheckOptions {
  /** Alles angehakt zeigt wieder den Standardtext, weil das Ergebnis dem
   *  ungefilterten Zustand entspricht. Nur setzen, wenn das fachlich stimmt:
   *  bei manchen Filtern ist "alles" eben nicht dasselbe wie "nichts". */
  allSelectedShowsDefault?: boolean;
}

export function initDropdownCheck(
  container: HTMLElement,
  onChange: (values: string[]) => void,
  opts?: DropdownCheckOptions
): DropdownCheckHandle {
  const btn = container.querySelector(".dropdown-check-btn") as HTMLButtonElement;
  const panel = container.querySelector(".dropdown-check-panel") as HTMLElement;
  const defaultLabel = btn.textContent || "";

  const boxes = () => Array.from(panel.querySelectorAll("input[type=checkbox]")) as HTMLInputElement[];
  const checked = () => boxes().filter(cb => cb.checked);

  function refreshLabel() {
    const all = boxes();
    const sel = all.filter(cb => cb.checked);
    const labels = sel.map(cb => (cb.parentElement!.textContent || "").trim());
    if (labels.length === 0) btn.textContent = defaultLabel;
    else if (opts?.allSelectedShowsDefault && sel.length === all.length) btn.textContent = defaultLabel;
    else if (labels.length === 1) btn.textContent = labels[0];
    else btn.textContent = `${labels[0]} +${labels.length - 1}`;
  }

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    document.querySelectorAll(".dropdown-check.open").forEach(d => { if (d !== container) d.classList.remove("open"); });
    container.classList.toggle("open");
  });

  panel.addEventListener("click", (e) => e.stopPropagation());

  panel.addEventListener("change", () => {
    refreshLabel();
    onChange(checked().map(cb => cb.value));
  });

  return {
    setValues(values: string[]) {
      const wanted = new Set(values);
      boxes().forEach(cb => { cb.checked = wanted.has(cb.value); });
      refreshLabel();
    },
    getValues() {
      return checked().map(cb => cb.value);
    },
  };
}

export interface DropdownSelectHandle {
  /** Setzt die Auswahl programmatisch. Loest bewusst kein onChange aus. */
  setValue(value: string): void;
  getValue(): string;
}

/** Einfachauswahl im selben Gewand wie initDropdownCheck, als Ersatz fuer ein
 *  natives <select>: dessen aufgeklappte Liste zeichnet das Betriebssystem, sie
 *  laesst sich also nicht an den Rest der Filterleiste angleichen.
 *  Eintraege sind <div class="dropdown-check-item" data-value="...">Label</div>,
 *  ein Klick waehlt und schliesst. Die Eintraege duerfen jederzeit ersetzt werden,
 *  der Klick laeuft ueber Delegation am Panel.
 */
export function initDropdownSelect(
  container: HTMLElement,
  onChange: (value: string) => void
): DropdownSelectHandle {
  const btn = container.querySelector(".dropdown-check-btn") as HTMLButtonElement;
  const panel = container.querySelector(".dropdown-check-panel") as HTMLElement;
  const defaultLabel = btn.textContent || "";
  let value = "";

  const items = () => Array.from(panel.querySelectorAll(".dropdown-check-item")) as HTMLElement[];

  function refresh() {
    const active = items().find(i => (i.dataset.value || "") === value);
    btn.textContent = active ? (active.textContent || "").trim() : defaultLabel;
    items().forEach(i => i.classList.toggle("is-active", i === active));
  }

  btn.addEventListener("click", (e) => {
    e.stopPropagation();
    document.querySelectorAll(".dropdown-check.open").forEach(d => { if (d !== container) d.classList.remove("open"); });
    container.classList.toggle("open");
  });

  panel.addEventListener("click", (e) => {
    e.stopPropagation();
    const item = (e.target as HTMLElement).closest(".dropdown-check-item") as HTMLElement | null;
    if (!item || !panel.contains(item)) return;
    value = item.dataset.value || "";
    refresh();
    container.classList.remove("open");
    onChange(value);
  });

  return {
    setValue(v: string) { value = v; refresh(); },
    getValue() { return value; },
  };
}
